import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { userLsKey } from '@/lib/userScopedStorage';

const BASE_KEY = 'sidebarCollapsed';
const LEGACY_KEY = BASE_KEY;

export function useSidebarCollapsed() {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  // Start with false; the user-scoped value is loaded in an effect once userId
  // is available so we never read another user's preference during lazy init.
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Track the current storage key so the StorageEvent listener always checks
  // the right key even when it captures the closure at mount time.
  const currentKeyRef = useRef<string>(LEGACY_KEY);

  // Read from user-scoped key once userId is known; clean up legacy unscoped key.
  useEffect(() => {
    const key = userLsKey(BASE_KEY, userId);
    currentKeyRef.current = key;
    try {
      const saved = localStorage.getItem(key);
      if (saved !== null) {
        setIsCollapsed(saved === 'true');
      }
      if (userId) {
        localStorage.removeItem(LEGACY_KEY);
      }
    } catch {
    }
  }, [userId]);

  // Persist whenever isCollapsed changes.
  useEffect(() => {
    try {
      localStorage.setItem(currentKeyRef.current, String(isCollapsed));
    } catch {
    }
    window.dispatchEvent(new CustomEvent('sidebarCollapsedChange', { detail: isCollapsed }));
  }, [isCollapsed]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === currentKeyRef.current) {
        setIsCollapsed(e.newValue === 'true');
      }
    };

    const handleCustomEvent = (e: CustomEvent<boolean>) => {
      setIsCollapsed(e.detail);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('sidebarCollapsedChange', handleCustomEvent as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarCollapsedChange', handleCustomEvent as EventListener);
    };
  }, []);

  const toggle = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const expand = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  return { isCollapsed, setIsCollapsed, toggle, collapse, expand };
}
